import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchPosts } from '@/lib/api';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function readTime(content) {
  if (!content) return '1 min read';
  const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
  const mins = Math.max(1, Math.ceil(words / 200));
  return `${mins} min read`;
}

function BlogSkeleton() {
  return (
    <>
      {/* Featured skeleton */}
      <div className="animate-pulse mb-16">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="bg-muted rounded-2xl h-72 lg:h-96" />
          <div className="space-y-4">
            <div className="bg-muted rounded h-4 w-24" />
            <div className="bg-muted rounded h-10 w-3/4" />
            <div className="bg-muted rounded h-4 w-full" />
            <div className="bg-muted rounded h-4 w-2/3" />
          </div>
        </div>
      </div>
      {/* Grid skeleton */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-muted rounded-xl h-48 mb-4" />
            <div className="bg-muted rounded h-6 w-3/4 mb-2" />
            <div className="bg-muted rounded h-4 w-full" />
          </div>
        ))}
      </div>
    </>
  );
}

function FeaturedPost({ post }) {
  return (
    <motion.article {...fadeInUp} className="mb-16">
      <Link to={`/blog/${post.slug}`} className="group block">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image */}
          <div className="relative overflow-hidden rounded-2xl shadow-lg">
            {post.cover_image ? (
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-72 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-700"
              />
            ) : (
              <div className="w-full h-72 lg:h-96 bg-primary/5 flex items-center justify-center">
                <span className="font-heading text-4xl text-primary/20">Dockside</span>
              </div>
            )}
            <div className="absolute top-4 left-4">
              <span className="bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">
                Latest
              </span>
            </div>
          </div>

          {/* Content */}
          <div>
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium uppercase tracking-wider text-accent"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-primary mb-4 group-hover:text-accent transition-colors duration-300">
              {post.title}
            </h2>
            {post.excerpt && (
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                {post.excerpt}
              </p>
            )}
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
              {post.author && <span className="font-medium text-foreground">{post.author}</span>}
              {post.publishedAt && (
                <>
                  <span>&middot;</span>
                  <span>{formatDate(post.publishedAt)}</span>
                </>
              )}
              <span>&middot;</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {readTime(post.content)}
              </span>
            </div>
            <span className="inline-flex items-center gap-2 text-accent font-medium group-hover:gap-3 transition-all">
              Read article <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

function PostCard({ post, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Link to={`/blog/${post.slug}`} className="group block h-full">
        <div className="h-full flex flex-col">
          {/* Image */}
          <div className="relative overflow-hidden rounded-xl mb-5">
            {post.cover_image ? (
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-52 bg-primary/5 rounded-xl flex items-center justify-center">
                <span className="font-heading text-2xl text-primary/20">Dockside</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex flex-col flex-1">
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {post.tags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium uppercase tracking-wider text-accent"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <h3 className="font-heading text-xl font-bold text-primary mb-2 group-hover:text-accent transition-colors duration-300">
              {post.title}
            </h3>
            {post.excerpt && (
              <p className="text-sm text-muted-foreground mb-4 flex-1 line-clamp-2">
                {post.excerpt}
              </p>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto pt-4 border-t border-border/50">
              {post.author && <span className="font-medium text-foreground">{post.author}</span>}
              {post.author && post.publishedAt && <span>&middot;</span>}
              {post.publishedAt && <span>{formatDate(post.publishedAt)}</span>}
              <span>&middot;</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {readTime(post.content)}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts()
      .then(setPosts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const remaining = posts.slice(1);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Insights & Ideas
            </h1>
            <p className="text-lg text-muted-foreground">
              Tips on web design, growing your online presence, and making the most
              of your business website.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <BlogSkeleton />
          ) : posts.length === 0 ? (
            <motion.div {...fadeInUp} className="text-center py-20">
              <p className="font-heading text-2xl text-primary mb-4">Coming soon</p>
              <p className="text-muted-foreground mb-8">
                We're working on our first articles. Check back soon!
              </p>
              <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </motion.div>
          ) : (
            <>
              {/* Featured post — first post gets a big layout */}
              {featured && <FeaturedPost post={featured} />}

              {/* Remaining posts in a grid */}
              {remaining.length > 0 && (
                <>
                  <motion.div {...fadeInUp}>
                    <div className="border-t border-border/50 pt-12 mb-8">
                      <h2 className="font-heading text-2xl font-bold text-primary">
                        More Articles
                      </h2>
                    </div>
                  </motion.div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {remaining.map((post, index) => (
                      <PostCard key={post.slug} post={post} index={index} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Your Business Online?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
              From {'\u00a3'}99/month with nothing to pay upfront. Let's build something great together.
            </p>
            <Button
              asChild
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
            >
              <Link to="/contact">Get a Quote</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
